import { CodeValue, Mode, PagedResponse, Permissions } from "@/types/common";
import { DepartmentTreeNode } from "./department";

export type InstructionMode = Mode;
export type InstructionPermissions = Permissions & {
  canChangeStatus: boolean;
  canConfirm: boolean;
  canReject: boolean;
  isApprover: boolean;
};

export enum INSTRUCTION_TYPE {
  INSTRUCTION = "INSTRUCTION", // 지시
  POLICY = "POLICY", // 방침
}

export enum INSTRUCTION_STATUS {
  WRITING = "WRITING", // 작성중
  PENDING_REVIEW = "PENDING_REVIEW", // 검토요청
  IN_PROGRESS = "IN_PROGRESS", // 진행중
  COMPLETION_REQUESTED = "COMPLETION_REQUESTED", // 완료요청
  CLOSED = "CLOSED", // 종결
  DELAYED = "DELAYED", // 지연
}

export enum INSTRUCTION_STATUS_TRANSITION {
  DELAYED_CLOSED = "DELAYED_CLOSED",
}

export interface AttachmentFile {
  id?: string;
  originalFilename: string;
  fileSize: number;
  status?: number;
  isProtected: boolean;
  tempId?: string; // for local file identification
}

export interface InstructionFormData {
  id?: number;
  type?: number;
  status?: number;
  uiType?: string;
  uiStatus?: string;
  title?: string;
  summary?: string;
  ownerDepartment?: DepartmentTreeNode;
  ccDepartments?: DepartmentTreeNode[];
  occurredDate?: string | null;
  dueDate?: string | null;
  reportDate?: string | null;
  createdDate?: string | null;
  detail?: string;
  report?: string;
  attachments?: AttachmentFile[];
}

export interface InstructionFormProps {
  id?: string;
  user: any;
  detailData: any;
  codeValues: any;
  initialData: InstructionFormData;
  onSubmit: (data: InstructionFormData, attachments: File[], submitType?: string | undefined) => void;
  onDelete: () => void;
  onCancel: () => void;
  onReject: (reason: string) => void;
  onExecuteRequest: () => void;
  onCompleteRequest: (data: InstructionFormData, attachments: File[]) => void;
  onComplete: () => void;
}

export interface InstructionSearchCondition {
  type?: number | null; // 지시유형
  status?: number[]; // 지시상태
  ownerDepartment?: number | null; // 수명부서
  ccDepartments?: number[]; // 협조부서
  occurredDate?: string | null; // 발생일
  dueDate?: string | null; // 기한일
  createdDate?: string | null; // 등록일
  keyword?: string; // 통합검색
}

export interface InstructionResponse {
  id: number;
  type: CodeValue; // 지시유형
  status: CodeValue; // 상태
  title: string; // 지시상황
  summary: string; // 지시요약
  ownerDepartment: CodeValue; // 수명부서
  ccDepartments: CodeValue[]; // 협조부서
  occurredDate: string; // 발생일
  dueDate: string; // 기한일
  reportDate: string; // 보고일자
  detail: string; // 상세 내용
  report: string; // 보고 내용
  rejectionReason: string; // 반려 내용
  attachments: AttachmentFile[];
  permissions: InstructionPermissions;
}

export interface InstructionListResponse {
  id: number;
  type: string; // 지시유형
  title?: string; // 지시상황
  summary?: string; // 지시요약
  ownerDepartment?: string; // 수명부서
  ccDepartments?: string; // 협조부서
  occurredDate?: string; // 발생일
  dueDate?: string; // 기한일
  closedDate?: string; // 종결일
  createdDate: string; // 등록일
  reportDate?: string; // 보고일자
  status: string; // 상태
}

export interface InstructionsResponse extends PagedResponse<InstructionListResponse> {}

export interface InstructionRejectRequest {
  id: number;
  reason: string;
}

export interface InstructionCompleteRequest {
  id: number;
  report: string;
  reportDate: string;
  attachments: AttachmentFile[];
}

export interface InstructionStatusChangeRequest {
  targetStatus: number;
}

export interface InstructionStatistics {
  total: number;
  pendingReview: number;
  inProgress: number;
  completionRequested: number;
  closed: number;
  delayed: number;
  delayedClosed: number;
}

export interface InstructionStatisticsResponse {
  owner: InstructionStatistics;
  entireCompany: InstructionStatistics;
}
